import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PageSection } from "@/components/site/PageSection";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Download, ExternalLink, Maximize2, X } from "lucide-react";
import JSZip from "jszip";
import socialCards from "@/data/social-cards.json";
import cardVariants from "@/data/social-card-variants.json";
import socialStoryImages from "@/data/social-story-images.json";

type SocialCard = {
  path: string;
  url: string;
  section: string;
  title: string;
  description: string;
  image: string;
  alt: string;
  createdAt?: string;
};

type SocialStoryImage = {
  title: string;
  description: string;
  image: string;
  alt: string;
  url: string;
  createdAt: string;
};

const SITE = "https://virginiabariatric.com";
const TITLE = "Page & Article Share Cards | Virginia Bariatric";
const DESCRIPTION =
  "Browse the social share card for every page and article on virginiabariatric.com, grouped by section with a full-screen preview.";

export const Route = createFileRoute("/social-cards")({
  component: SocialCardsGallery,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "noindex, follow" },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `${SITE}/social-cards` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ] : []),
    ],
    links: [{ rel: "canonical", href: `${SITE}/social-cards` }],
  }),
});

/** Serve same-origin images from the current host so previews work too. */
const localSrc = (image: string) =>
  image.startsWith(SITE) ? image.slice(SITE.length) : image;

const ALL = "All sections";

type FormatKey = "link" | "portrait" | "square" | "wide";

const FORMATS: {
  key: FormatKey;
  label: string;
  note: string;
  ratio: string;
}[] = [
  { key: "link", label: "Link preview", note: "1.91:1 — Facebook, LinkedIn, X", ratio: "1200/630" },
  { key: "portrait", label: "Instagram 4:5", note: "1080×1350 — Instagram feed", ratio: "1080/1350" },
  { key: "square", label: "Square 1:1", note: "1440×1440 — Instagram grid, WhatsApp", ratio: "1/1" },
  { key: "wide", label: "Wide 16:9", note: "1920×1080 — YouTube, Pinterest", ratio: "16/9" },
];

const variants = cardVariants as Record<string, Partial<Record<Exclude<FormatKey, "link">, string>>>;

/** Image source for a card in the selected format, falling back to the link card. */
const imageFor = (card: SocialCard, format: FormatKey) =>
  format === "link" ? localSrc(card.image) : variants[card.path]?.[format] ?? localSrc(card.image);

/** Slug used as the ZIP and image filename base, derived from the page path. */
const cardSlug = (card: SocialCard) =>
  card.path.replace(/^\//, "").replace(/\//g, "-") || "home";

/** Fetch all four crops and download them as a single ZIP file. */
async function downloadAllCrops(card: SocialCard) {
  const base = cardSlug(card);
  const zip = new JSZip();
  await Promise.all(
    FORMATS.map(async (f) => {
      const src = imageFor(card, f.key);
      const response = await fetch(src);
      if (!response.ok) throw new Error(`Failed to fetch ${src}: ${response.status}`);
      const blob = await response.blob();
      const ext = src.split(".").pop()?.split("?")[0] || "png";
      zip.file(`${base}-${f.key}.${ext}`, blob);
    })
  );
  const content = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(content);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${base}-share-cards.zip`;
  a.click();
  URL.revokeObjectURL(url);
}

function SocialCardsGallery() {
  const cards = useMemo(
    () =>
      [...(socialCards as SocialCard[])].sort((a, b) => {
        if (a.createdAt && b.createdAt) return b.createdAt.localeCompare(a.createdAt);
        if (a.createdAt) return -1;
        if (b.createdAt) return 1;
        return 0;
      }),
    []
  );
  const sections = useMemo(
    () => [ALL, ...Array.from(new Set(cards.map((c) => c.section)))],
    [cards]
  );
  const [section, setSection] = useState(ALL);
  const [format, setFormat] = useState<FormatKey>("link");
  const activeFormat = FORMATS.find((f) => f.key === format)!;
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [zipping, setZipping] = useState<string | null>(null);
  const storyImages = useMemo(
    () =>
      [...(socialStoryImages as SocialStoryImage[])].sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt)
      ),
    []
  );

  const handleZip = (card: SocialCard) => {
    if (zipping) return;
    setZipping(card.path);
    downloadAllCrops(card)
      .catch((error) => console.error("Share card ZIP failed", error))
      .finally(() => setZipping(null));
  };

  const visible = useMemo(
    () => (section === ALL ? cards : cards.filter((c) => c.section === section)),
    [cards, section]
  );

  const step = useCallback(
    (delta: number) =>
      setOpenIndex((current) =>
        current === null ? current : (current + delta + visible.length) % visible.length
      ),
    [visible.length]
  );

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenIndex(null);
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openIndex, step]);

  const active = openIndex === null ? null : visible[openIndex];

  return (
    <main>
      <PageSection className="py-12">
        <header className="text-center">
          <h1 className="text-3xl font-bold text-brand-logo md:text-4xl">
            Page &amp; Article Share Cards
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            The social preview image every page and article shows when it is shared on Facebook,
            LinkedIn, or X — plus the Instagram 4:5, square and 16:9 crops of the same card.
            Pick a size, then select a card to view it full screen or download it.
          </p>
        </header>

        <nav
          className="mt-8 flex flex-wrap items-center justify-center gap-2"
          aria-label="Filter cards by section"
        >
          {sections.map((name) => (
            <Button
              key={name}
              size="sm"
              variant={name === section ? "default" : "outline"}
              aria-pressed={name === section}
              onClick={() => {
                setSection(name);
                setOpenIndex(null);
              }}
            >
              {name}
            </Button>
          ))}
        </nav>

        <nav
          className="mt-4 flex flex-wrap items-center justify-center gap-2"
          aria-label="Choose card size"
        >
          {FORMATS.map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant={f.key === format ? "default" : "outline"}
              aria-pressed={f.key === format}
              onClick={() => setFormat(f.key)}
            >
              {f.label}
            </Button>
          ))}
        </nav>

        <p className="mt-4 text-center text-sm text-muted-foreground">
          Showing {visible.length} of {cards.length} cards · {activeFormat.note}
        </p>

        <section className="mt-10" aria-labelledby="story-images-heading">
          <header className="text-center">
            <h2 id="story-images-heading" className="text-2xl font-bold text-foreground">
              Instagram &amp; Facebook Story Images
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">9:16 · 1080×1920</p>
          </header>
          <ul className="mx-auto mt-6 grid max-w-3xl gap-6 sm:grid-cols-2">
            {storyImages.map((story) => (
              <li
                key={story.image}
                className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm"
              >
                <img
                  src={story.image}
                  alt={story.alt}
                  width={1080}
                  height={1920}
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                  className="aspect-[9/16] w-full bg-muted object-contain"
                />
                <section className="flex flex-1 flex-col gap-2 p-4 text-center">
                  <h3 className="text-base font-semibold text-foreground">{story.title}</h3>
                  <p className="text-sm text-muted-foreground">{story.description}</p>
                  <Button asChild variant="outline" size="sm" className="mt-auto">
                    <a href={story.image} download>
                      <Download className="h-3.5 w-3.5" aria-hidden="true" />
                      Download Story Image
                    </a>
                  </Button>
                </section>
              </li>
            ))}
          </ul>
        </section>

        <ul className="mt-8 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {visible.map((card, index) => (
            <li
              key={card.path}
              className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm"
            >
              <button
                type="button"
                onClick={() => setOpenIndex(index)}
                className="group relative block w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`View the ${card.title} share card full screen`}
              >
                <img
                  src={imageFor(card, format)}
                  alt={card.alt}
                  loading="lazy"
                  decoding="async"
                  style={{ aspectRatio: activeFormat.ratio }}
                  className="w-full bg-muted object-contain transition-opacity group-hover:opacity-90"
                />

                <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-background/85 p-2 text-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                  <Maximize2 className="h-4 w-4" aria-hidden="true" />
                </span>
              </button>
              <div className="flex flex-1 flex-col gap-2 p-4 text-center">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {card.section}
                </p>
                <h2 className="text-base font-semibold text-foreground">{card.title}</h2>
                <p className="line-clamp-2 text-sm text-muted-foreground">{card.description}</p>
                <a
                  href={card.path}
                  className="mt-auto inline-flex items-center justify-center gap-1 pt-2 text-sm font-medium text-brand-logo underline underline-offset-4"
                >
                  Open {card.path}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
                <a
                  href={imageFor(card, format)}
                  download
                  className="inline-flex items-center justify-center gap-1 text-sm font-medium text-muted-foreground underline underline-offset-4"
                >
                  Download {activeFormat.label} image
                </a>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-1 gap-1.5"
                  disabled={zipping !== null}
                  onClick={() => handleZip(card)}
                  aria-label={`Download all four share card sizes for ${card.title} as a ZIP file`}
                >
                  <Download className="h-3.5 w-3.5" aria-hidden="true" />
                  {zipping === card.path ? "Preparing ZIP…" : "Download all 4 sizes (ZIP)"}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </PageSection>

      {active && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${active.title} share card`}
          className="fixed inset-0 z-50 flex flex-col bg-foreground/95 p-4 md:p-8"
          onClick={() => setOpenIndex(null)}
        >
          <div
            className="flex flex-1 flex-col items-center justify-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={imageFor(active, format)}
              alt={active.alt}
              className="max-h-[70vh] w-auto max-w-full rounded-lg shadow-2xl"
            />
            <p className="max-w-2xl text-center text-sm text-background">
              <strong>{active.title}</strong> — {active.path} · {activeFormat.note}
            </p>
            <div className="flex items-center gap-3">
              <Button variant="secondary" onClick={() => step(-1)} aria-label="Previous card">
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                Prev
              </Button>
              <span className="text-sm text-background">
                {(openIndex ?? 0) + 1} / {visible.length}
              </span>
              <Button variant="secondary" onClick={() => step(1)} aria-label="Next card">
                Next
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
          <Button
            variant="secondary"
            size="icon"
            className="absolute right-4 top-4"
            onClick={() => setOpenIndex(null)}
            aria-label="Close full-screen preview"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      )}
    </main>
  );
}
